import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { createClient } from "@supabase/supabase-js";
import Topbar from "./Topbar";
import Footer from "./Footer";
import Loader from "./Loader";
const apiBase = process.env.REACT_APP_API_URL;

const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
});

const AroundTheLeague = () => {
  const { leagueId } = useParams(); // /league/:leagueId/around
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openMatchupId, setOpenMatchupId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        // 1️⃣ Get fresh access token
        const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();

        if (sessionErr || !sessionData?.session) {
          throw new Error("Not authenticated");
        }

        const token = sessionData.session.access_token;

        // 2️⃣ Call backend (POST + body)
        const res = await axios.post(
          `${apiBase}/api/league/matchup-results`,
          { league_id: leagueId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        setData(res.data);
      } catch (err) {
        console.error("Failed to load Around The League:", err);
        setError("Failed to load matchup results");
      } finally {
        setLoading(false);
      }
    };

    if (leagueId) {
      load();
    }
  }, [leagueId]);

  const sortByRole = useCallback((a, b) => {
    const ROLE_ORDER = ["Top", "Jng", "Mid", "Bot", "Sup"];
    const aIndex = ROLE_ORDER.indexOf(a.role);
    const bIndex = ROLE_ORDER.indexOf(b.role);

    // Unknown roles go to the end
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  }, []);

  const toggleMatchup = (matchupId) => {
    setOpenMatchupId((prev) => (prev === matchupId ? null : matchupId));
  };

  const getPlayerImage = (player) => {
    // If user has a skin equipped → use it
    if (player?.equipped_skin?.full_preview) {
      return `/players/${player.equipped_skin.full_preview}`;
    }

    // Otherwise fallback to base player render
    if (player?.player_image) {
      return `/players/${player.player_image}`;
    }

    return "/default-player.png";
  };

  const MatchupPlayersDropdown = ({ matchup, isOpen, onToggle }) => {
    return (
      <div className="matchup-dropdown">
        <button className="matchup-dropdown-toggle" onClick={onToggle}>
          <span>View Starters</span>
          <span>{isOpen ? "▲" : "▼"}</span>
        </button>

        {isOpen && (
          <div className="matchup-dropdown-content">
            <div className="matchup-columns">
              {/* Home team */}
              <div className="team-column" style={{ alignItems: "flex-end" }}>
                <div className="team-column-header" style={{ marginRight: "25px" }}>
                  <span>{matchup.home_team.display_name}</span>
                  <img src={`/avatars/${matchup.home_team.profile_image}`} alt="Profile Avatar" />
                </div>

                {!matchup.home_team.players?.length ? (
                  <div className="empty">No starters</div>
                ) : (
                  matchup.home_team.players
                    ?.slice()
                    .sort(sortByRole)
                    .map((p) => (
                      <div key={p.id} className="player-row">
                        <div style={{ display: "flex", alignItems: "flex-end", flexDirection: "column" }}>
                          <img src={`/boosts/${p?.item_image}`} className="boost" alt={p?.boost_id} />
                          <div className="player-name">{p.name}</div>
                          <div className="player-sub" style={{ textAlign: "end" }}>
                            {p.points?.toFixed(1) || 0} pts
                          </div>
                        </div>
                        <img src={getPlayerImage(p)} alt={p.name} className="player-image" />

                        <div className="vs-divider">
                          <img src={`/roles/${p.role}.svg`} alt={p.role} style={{ width: "14px", height: "14px", objectFit: "contain" }} />
                        </div>
                      </div>
                    ))
                )}
              </div>

              {/* Away team */}
              <div className="team-column" style={{ alignItems: "flex-start" }}>
                <div className="team-column-header">
                  <img src={`/avatars/${matchup.away_team.profile_image}`} alt="Profile Avatar" />
                  <span>{matchup.away_team.display_name}</span>
                </div>

                {!matchup.away_team.players?.length ? (
                  <div className="empty">No starters</div>
                ) : (
                  matchup.away_team.players
                    ?.slice()
                    .sort(sortByRole)
                    .map((p) => (
                      <div key={p.id} style={{ display: "flex", alignItems: "center" }}>
                        <div className="player-row">
                          <img src={getPlayerImage(p)} alt={p.name} className="player-image" />

                          <div style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
                            <img src={`/boosts/${p?.item_image}`} className="boost" alt={p?.boost_id} />
                            <div className="player-name">{p.name}</div>
                            <div className="player-sub">{p.points?.toFixed(1) || 0} pts</div>
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const SuperscriptNumber = ({ value, color }) => {
    if (value === null || value === undefined || value === "-") {
      return <span>-</span>;
    }

    const [intPart, decimalPart] = Number(value).toFixed(2).split(".");

    return (
      <span style={{ display: "inline-flex", alignItems: "flex-start" }}>
        <span>{intPart}</span>
        <span
          style={{
            fontSize: "16px",
            marginLeft: "6px",
            position: "relative",
            top: "6px",
            opacity: 0.9,
            color,
          }}>
          .{decimalPart}
        </span>
      </span>
    );
  };

  if (loading) return <Loader loadingTitle={"Finding League Results"} showLogo={true} />;

  if (error) {
    return <p style={{ color: "#ef4444" }}>{error}</p>;
  }

  return (
    <div className="page-container">
      <Topbar leagueId={leagueId} showLinks={true} showBudget={true} />
      <div className="main">
        <div className="atl-header">
          <h4 style={{ color: "#fff", marginBottom: "10px", margin: "0" }}>LEAGUE RESULTS</h4>
          {data.activeWeek && (
            <span>
              Week {data.activeWeek.week} • {data.activeWeek.week_start} – {data.activeWeek.week_end}
            </span>
          )}
        </div>
        {!data?.aroundTheLeague?.length ? (
          <p style={{ color: "#8899a8", marginTop: "20px" }}>No matchup results available for this week.</p>
        ) : (
          <div className="atl-list">
            {data.aroundTheLeague.map((m) => {
              const homeWon = m.winner_team_id === m.home_team_id;
              const awayWon = m.winner_team_id === m.away_team_id;

              return (
                <div style={{ display: "flex", flexDirection: "column", padding: "10px" }} key={m.matchup_id} className={`atl-card`}>
                  <div className={`atl-card ${m.is_user_matchup ? "user-matchup" : ""}`}>
                    <div className={`team-matchup ${homeWon ? "winner" : ""}`}>
                      <div style={{ display: "flex", alignItems: "center", flexDirection: "column", gap: "4px" }}>
                        <img
                          src={`/avatars/${m.home_team.profile_image}`}
                          style={{ height: "35px", borderRadius: "3px", objectFit: "contain", border: "1px solid #19232f" }}
                          alt="Profile"
                        />
                        <span className="team-name">{m.home_team.display_name}</span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <h2
                          style={{
                            color: "white",
                            margin: "0",
                            fontSize: "24px",
                            fontWeight: "800",
                            fontStyle: "italic",
                          }}>
                          <SuperscriptNumber value={m.home_points} color="white" />
                        </h2>
                      </div>
                    </div>
                    <div className="pts-divider">
                      <img src="/vs-vector.png" style={{ width: "32px", opacity: "0.2" }} alt="VS" />
                    </div>

                    <div className={`team-matchup ${awayWon ? "winner" : ""}`}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <h2
                          style={{
                            color: "white",
                            margin: "0",
                            fontSize: "24px",
                            fontWeight: "800",
                            fontStyle: "italic",
                          }}>
                          <SuperscriptNumber value={m.away_points} color="white" />
                        </h2>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", flexDirection: "column", gap: "4px" }}>
                        <img
                          src={`/avatars/${m.away_team.profile_image}`}
                          style={{ height: "35px", borderRadius: "3px", objectFit: "contain", border: "1px solid #19232f" }}
                          alt="Profile"
                        />
                        <span className="team-name">{m.away_team.display_name}</span>
                      </div>
                    </div>
                  </div>
                  <MatchupPlayersDropdown matchup={m} isOpen={openMatchupId === m.matchup_id} onToggle={() => toggleMatchup(m.matchup_id)} />
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AroundTheLeague;
